import { useEffect, useMemo, useState } from 'react';
import { FeatureFlag } from '../constants/featureFlags';
import { VISIT_TYPES_REQUIRING_VERSIONED_FORM_SCHEMAS } from '../constants/visitTypes';
import { useStore } from '../store';
import { client } from '../services/api';
import { getVisitTypeOptionsVisitsTypeOptionsGet } from '../services/api/sdk.gen';
import type { VisitTypeId, VisitTypeOption } from '../services/api/types.gen';

let cachedOptions: VisitTypeOption[] | null = null;
let cachedDisplayNames: Record<string, string> | null = null;
let fetchPromise: Promise<VisitTypeOption[]> | null = null;

async function fetchOptions(): Promise<VisitTypeOption[]> {
  if (cachedOptions) return cachedOptions;
  if (fetchPromise) return fetchPromise;

  fetchPromise = getVisitTypeOptionsVisitsTypeOptionsGet({ client })
    .then(res => {
      const rawData: unknown = res.data;
      if (!Array.isArray(rawData)) {
        throw new Error('Unexpected visit type options payload shape');
      }
      const data = rawData as VisitTypeOption[];
      cachedOptions = data;
      cachedDisplayNames = Object.fromEntries(
        data.map(o => [o.id, o.display_name])
      );
      return data;
    })
    .catch(() => {
      fetchPromise = null;
      return [];
    });

  return fetchPromise;
}

/**
 * Hook that fetches visit type options from the backend and caches them.
 * Returns the options list and a display name lookup function.
 */
export function useVisitTypeOptions() {
  const hasVersionedFormSchemas = useStore(state =>
    state.hasFeatureFlag(FeatureFlag.VERSIONED_FORM_SCHEMAS)
  );
  const [fetchedOptions, setFetchedOptions] = useState<VisitTypeOption[]>(
    cachedOptions ?? []
  );
  const [loading, setLoading] = useState(!cachedOptions);

  useEffect(() => {
    if (cachedOptions) {
      setFetchedOptions(cachedOptions);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchOptions().then(data => {
      if (!cancelled) {
        setFetchedOptions(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const options = useMemo(() => {
    if (hasVersionedFormSchemas) return fetchedOptions;
    return fetchedOptions.filter(
      o => !VISIT_TYPES_REQUIRING_VERSIONED_FORM_SCHEMAS.has(o.id)
    );
  }, [fetchedOptions, hasVersionedFormSchemas]);

  return { options, loading };
}

/**
 * Whether this visit type has a RogerDoc form schema (from ``/visits/type-options``).
 * Returns false if ``options`` is not loaded yet or the id is missing (safe default).
 */
export function visitTypeUsesRogerDocForm(
  visitType: VisitTypeId,
  options: ReadonlyArray<VisitTypeOption>
): boolean {
  return options.find(o => o.id === visitType)?.uses_roger_doc_form ?? false;
}

/**
 * Get a human-readable display name for a visit type ID.
 * Uses the cached backend data when available, falls back to basic formatting.
 */
export function getVisitTypeDisplayName(visitType: string): string {
  if (cachedDisplayNames && visitType in cachedDisplayNames) {
    return cachedDisplayNames[visitType];
  }
  return visitType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}
